import type { ActivityDTO } from 'src/types/dto/activity.dto';
import type { AppointmentDTO } from 'src/types/dto/appointment.dto';
import type { InspectorDTO } from 'src/types/dto/inspector.dto';
import type {
  RescheduleChangeDTO,
  RescheduleSuggestionDTO,
} from 'src/types/dto/reschedule-suggestion.dto';

import dayjs from 'dayjs';

import {
  formatRescheduleDetailDate,
  getActivityCurrentAppointments,
  getEarliestNonBlockerAppointment,
} from 'src/components/inspector-reschedule-card/utils';

export type RescheduleConfirmationStepKey =
  | 'cancelAppointment'
  | 'cancelProposedExistingAppointment'
  | 'scheduleAppointment';

export type RescheduleConfirmationStep = {
  stepNumber: number;
  key: RescheduleConfirmationStepKey;
  referenceId: number;
  person: string;
  date?: string;
};

const resolveChangeActivity = (
  change: RescheduleChangeDTO,
  currentActivity: ActivityDTO
): ActivityDTO => (change.activity.id === currentActivity.id ? currentActivity : change.activity);

const getCurrentAppointmentsForChange = (
  change: RescheduleChangeDTO,
  currentActivity: ActivityDTO
): AppointmentDTO[] => {
  const resolvedActivity = resolveChangeActivity(change, currentActivity);
  const activityAppointments = getActivityCurrentAppointments(resolvedActivity);

  if (activityAppointments.length > 0) {
    return activityAppointments;
  }

  return change.oldAppointment ?? [];
};

const getCurrentInspectorForChange = (
  change: RescheduleChangeDTO,
  currentActivity: ActivityDTO,
  currentPrimaryAppointment?: AppointmentDTO
): InspectorDTO | undefined => {
  if (currentPrimaryAppointment?.inspector) {
    return currentPrimaryAppointment.inspector;
  }

  const resolvedActivity = resolveChangeActivity(change, currentActivity);

  return (
    resolvedActivity.allocatedInspectors?.[0] ??
    getEarliestNonBlockerAppointment(currentActivity.appointments)?.inspector ??
    getEarliestNonBlockerAppointment(change.oldAppointment)?.inspector
  );
};

const getOldAppointmentsForChange = (change: RescheduleChangeDTO): AppointmentDTO[] =>
  change.oldAppointment?.length ? change.oldAppointment : getActivityCurrentAppointments(change.activity);

const getSecondaryActivityChanges = (
  suggestion: RescheduleSuggestionDTO,
  currentActivity: ActivityDTO
): RescheduleChangeDTO[] =>
  suggestion.changes.filter((change) => change.activity.id !== currentActivity.id);

const getProposedInspectorExistingAppointmentToCancel = (
  suggestion: RescheduleSuggestionDTO,
  currentActivity: ActivityDTO,
  proposedInspector: InspectorDTO,
  proposedPrimaryAppointment?: AppointmentDTO
): { appointment: AppointmentDTO; activityId: number } | undefined => {
  for (const change of suggestion.changes) {
    if (change.activity.id === currentActivity.id) {
      continue;
    }

    const oldAppointments = getOldAppointmentsForChange(change);
    const inspectorAppointments = oldAppointments.filter(
      (appointment) => appointment.inspector?.id === proposedInspector.id
    );
    const appointment =
      getEarliestNonBlockerAppointment(inspectorAppointments) ??
      getEarliestNonBlockerAppointment(oldAppointments);

    if (appointment) {
      return { appointment, activityId: change.activity.id };
    }
  }

  const proposedFrom = proposedPrimaryAppointment?.from;
  const inspectorAppointments =
    proposedInspector.appointments?.filter(
      (appointment) =>
        appointment.type !== 'blocker' &&
        appointment.activity?.id !== currentActivity.id &&
        (!appointment.inspector?.id || appointment.inspector.id === proposedInspector.id)
    ) ?? [];

  if (inspectorAppointments.length === 0) {
    return undefined;
  }

  const onProposedDate = proposedFrom
    ? inspectorAppointments.find((appointment) =>
        dayjs(appointment.from).isSame(dayjs(proposedFrom), 'day')
      )
    : undefined;
  const appointment = onProposedDate ?? getEarliestNonBlockerAppointment(inspectorAppointments);

  if (!appointment) {
    return undefined;
  }

  return {
    appointment,
    activityId: appointment.activity?.id ?? currentActivity.id,
  };
};

export const buildRescheduleConfirmationSteps = (
  suggestion: RescheduleSuggestionDTO,
  currentActivity: ActivityDTO
): RescheduleConfirmationStep[] => {
  const change =
    suggestion.changes.find((item) => item.activity.id === currentActivity.id) ??
    suggestion.changes[0];

  if (!change) {
    return [];
  }

  const currentAppointments = getCurrentAppointmentsForChange(change, currentActivity);
  const currentPrimaryAppointment = getEarliestNonBlockerAppointment(currentAppointments);
  const proposedPrimaryAppointment = getEarliestNonBlockerAppointment(change.newAppointment);
  const currentInspector = getCurrentInspectorForChange(
    change,
    currentActivity,
    currentPrimaryAppointment
  );
  const proposedInspector = suggestion.inspector;
  const proposedExistingAppointmentToCancel = getProposedInspectorExistingAppointmentToCancel(
    suggestion,
    currentActivity,
    proposedInspector,
    proposedPrimaryAppointment
  );

  const steps: RescheduleConfirmationStep[] = [];
  const cancelledAppointmentIds = new Set<number>();

  const appendStep = (step: Omit<RescheduleConfirmationStep, 'stepNumber'>) => {
    steps.push({ ...step, stepNumber: steps.length + 1 });
  };

  const appendCancelAppointmentStep = (
    change: RescheduleChangeDTO,
    appointment: AppointmentDTO,
    person: string
  ) => {
    const appointmentId = appointment.id;
    if (appointmentId != null && cancelledAppointmentIds.has(appointmentId)) {
      return;
    }

    if (appointmentId != null) {
      cancelledAppointmentIds.add(appointmentId);
    }

    appendStep({
      key: 'cancelAppointment',
      referenceId: change.activity.id,
      person,
      date: formatRescheduleDetailDate(appointment.from),
    });
  };

  if (currentInspector && currentPrimaryAppointment) {
    appendCancelAppointmentStep(change, currentPrimaryAppointment, currentInspector.name);
  }

  const proposedExistingAppointment = proposedExistingAppointmentToCancel?.appointment;
  if (
    proposedExistingAppointment &&
    (proposedExistingAppointment.id == null ||
      !cancelledAppointmentIds.has(proposedExistingAppointment.id))
  ) {
    if (proposedExistingAppointment.id != null) {
      cancelledAppointmentIds.add(proposedExistingAppointment.id);
    }

    appendStep({
      key: 'cancelProposedExistingAppointment',
      referenceId: proposedExistingAppointmentToCancel.activityId,
      person: proposedInspector.name,
      date: formatRescheduleDetailDate(proposedExistingAppointment.from),
    });
  }

  if (proposedPrimaryAppointment) {
    appendStep({
      key: 'scheduleAppointment',
      referenceId: change.activity.id,
      person: proposedInspector.name,
      date: formatRescheduleDetailDate(proposedPrimaryAppointment.from),
    });
  }

  getSecondaryActivityChanges(suggestion, currentActivity).forEach((secondaryChange) => {
    const secondaryCurrentAppointments = getCurrentAppointmentsForChange(
      secondaryChange,
      currentActivity
    );
    const secondaryPrimaryAppointment = getEarliestNonBlockerAppointment(
      secondaryCurrentAppointments
    );
    const secondaryInspector = secondaryPrimaryAppointment?.inspector;

    if (secondaryInspector && secondaryPrimaryAppointment) {
      appendCancelAppointmentStep(
        secondaryChange,
        secondaryPrimaryAppointment,
        secondaryInspector.name
      );
    }

    const secondaryProposedAppointment = getEarliestNonBlockerAppointment(
      secondaryChange.newAppointment
    );

    if (!secondaryProposedAppointment) {
      return;
    }

    appendStep({
      key: 'scheduleAppointment',
      referenceId: secondaryChange.activity.id,
      person: secondaryProposedAppointment.inspector?.name ?? proposedInspector.name,
      date: formatRescheduleDetailDate(secondaryProposedAppointment.from),
    });
  });

  return steps;
};
